import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Users, ShieldCheck, Sparkles, Activity, BadgeEuro, Search, X, Save, Plus,
  AlertTriangle, Ban, RefreshCw, BedDouble, TrendingUp, BarChart3, Settings as SettingsIcon,
  Mail, Power, FileWarning, ChevronRight, Handshake, Wallet, Copy, Check, Loader2, Copy as CopyIcon, GitMerge, Trash2,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, Input, Label, Spinner, Badge, Select } from "../components/ui/Primitives";
import { formatDate, formatDateTime, formatEUR } from "../utils/format";

// -------------------- KPI tile --------------------
function Kpi({ icon: Icon, label, value, sub, accent = false, tone = "default", testId }) {
  const toneClass = {
    default: "bg-white/5 text-ink-secondary",
    brand: "bg-brand/15 text-brand",
    ok: "bg-ok/15 text-ok",
    warn: "bg-warn/15 text-warn",
    err: "bg-err/15 text-err",
  }[tone];
  return (
    <div data-testid={testId} className="relative bg-bg-surface border border-white/10 rounded-xl p-5 overflow-hidden lift hover:border-white/20 transition-colors">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-ink-secondary font-medium">{label}</div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${accent ? "bg-brand/15 text-brand" : toneClass}`}>
          <Icon size={14} strokeWidth={1.6} />
        </div>
      </div>
      <div className="num text-2xl text-ink-primary mt-2 font-medium tracking-tight" data-testid={`${testId}-value`}>{value}</div>
      {sub && <div className="text-xs text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

// -------------------- Inline bar chart (SVG, no extra dep) --------------------
function MRRBarChart({ data }) {
  const maxVal = Math.max(1, ...data.map((b) => b.total_eur));
  const W = 720, H = 220, padL = 40, padB = 28, padT = 12;
  const innerW = W - padL - 16;
  const innerH = H - padT - padB;
  const barW = (innerW / data.length) * 0.65;
  const slot = innerW / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="MRR history" data-testid="mrr-chart">
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
        const y = padT + innerH * (1 - p);
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - 8} y2={y} stroke="rgba(255,255,255,0.06)" />
            <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="9" fill="#7d8590">
              {Math.round(maxVal * p)}
            </text>
          </g>
        );
      })}
      {data.map((b, i) => {
        const subH = (b.subscription_eur / maxVal) * innerH;
        const topH = (b.topup_eur / maxVal) * innerH;
        const x = padL + slot * i + (slot - barW) / 2;
        const ySub = padT + innerH - subH;
        const yTop = ySub - topH;
        return (
          <g key={b.label}>
            <rect x={x} y={ySub} width={barW} height={Math.max(subH, 0)} fill="#3B82F6" rx="2" />
            <rect x={x} y={yTop} width={barW} height={Math.max(topH, 0)} fill="#8B5CF6" rx="2" />
            <text x={x + barW / 2} y={H - padB + 14} textAnchor="middle" fontSize="9" fill="#7d8590">
              {b.label.slice(5)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// -------------------- Edit User Modal --------------------
function EditUserModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState(user.plan || "none");
  const [bonus, setBonus] = useState("");
  const [bonusReason, setBonusReason] = useState("");
  const [demoEnabled, setDemoEnabled] = useState(user.demo_mode || false);
  const [demoDays, setDemoDays] = useState(14);
  const [notes, setNotes] = useState(user.notes || "");
  const [saving, setSaving] = useState(false);
  const PLANS = ["none", "kueche", "bar", "pro", "hotel", "premium"];

  const savePlanAndNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { plan, notes });
      toast.success(t("admin.saved"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const grantBonus = async () => {
    const n = Number(bonus);
    if (!n || n <= 0) return toast.error(t("admin.invalid_credits"));
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/bonus-credits`, { credits: n, reason: bonusReason });
      toast.success(t("admin.bonus_granted", { n }));
      setBonus(""); setBonusReason("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setDemo = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/demo`, { enabled: demoEnabled, days: demoEnabled ? Number(demoDays) : null });
      toast.success(t("admin.saved"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleSuspend = async () => {
    setSaving(true);
    try {
      const url = user.is_active ? `/admin/users/${user.id}/suspend` : `/admin/users/${user.id}/reactivate`;
      await api.post(url);
      toast.success(t("admin.saved"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deleteUser = async () => {
    if (!window.confirm(t("admin.delete_confirm", { email: user.email }))) return;
    setSaving(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(t("admin.deleted"));
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-void/80 backdrop-blur-sm px-4" data-testid="edit-user-modal">
      <div className="bg-bg-surface border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-white/10 sticky top-0 bg-bg-surface z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("admin.edit_user")}</div>
            <div className="text-base font-semibold text-ink-primary truncate max-w-[400px]">{user.email}</div>
            <div className="text-xs text-ink-secondary mt-0.5 flex gap-2">
              <Badge tone={user.is_active ? "ok" : "err"}>{user.is_active ? t("admin.active") : t("admin.suspended")}</Badge>
              <Badge>{user.role}</Badge>
              <Badge tone="brand">{user.plan || "none"}</Badge>
              {user.demo_mode && <Badge tone="warn">DEMO</Badge>}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary p-1" data-testid="close-edit-modal">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Plan */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-ink-muted mb-2 font-medium">{t("admin.section_plan")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("billing.your_plan")}</Label>
                <Select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="modal-plan-select">
                  {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={savePlanAndNotes} disabled={saving} data-testid="modal-save-plan">
                  <Save size={14} /> {t("admin.save_plan_notes")}
                </Button>
              </div>
            </div>
          </section>

          {/* Bonus credits */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-ink-muted mb-2 font-medium">{t("admin.section_bonus")}</h3>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <Label>{t("admin.credits")}</Label>
                <Input type="number" min="1" max="10000" value={bonus} onChange={(e) => setBonus(e.target.value)} data-testid="modal-bonus-credits" />
              </div>
              <div>
                <Label>{t("admin.reason")}</Label>
                <Input value={bonusReason} onChange={(e) => setBonusReason(e.target.value)} placeholder={t("admin.reason_placeholder")} data-testid="modal-bonus-reason" />
              </div>
              <Button variant="secondary" onClick={grantBonus} disabled={saving} data-testid="modal-grant-bonus">
                <Plus size={14} /> {t("admin.grant")}
              </Button>
            </div>
            <div className="text-xs text-ink-muted mt-2">
              {t("admin.current_credits")}: <span className="num text-ink-secondary">{(user.ai_credits || 0) + (user.purchased_credits || 0)}</span>
              {" "}({user.ai_credits || 0} Plan + {user.purchased_credits || 0} Top-up)
            </div>
          </section>

          {/* Demo mode */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-ink-muted mb-2 font-medium">{t("admin.section_demo")}</h3>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-ink-secondary mb-2 cursor-pointer">
                <input type="checkbox" checked={demoEnabled} onChange={(e) => setDemoEnabled(e.target.checked)} data-testid="modal-demo-toggle" className="accent-brand" />
                {t("admin.demo_enabled")}
              </label>
              {demoEnabled && (
                <div className="w-32">
                  <Label>{t("admin.demo_days")}</Label>
                  <Input type="number" min="1" max="365" value={demoDays} onChange={(e) => setDemoDays(e.target.value)} data-testid="modal-demo-days" />
                </div>
              )}
              <Button variant="secondary" onClick={setDemo} disabled={saving} data-testid="modal-save-demo">
                <BedDouble size={14} /> {t("admin.apply")}
              </Button>
            </div>
            {user.demo_expires_at && (
              <div className="text-xs text-ink-muted mt-2">
                {t("admin.demo_current")}: {formatDateTime(user.demo_expires_at)}
              </div>
            )}
          </section>

          {/* Notes */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-ink-muted mb-2 font-medium">{t("admin.section_notes")}</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="modal-notes"
              placeholder={t("admin.notes_placeholder")}
              className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:border-brand focus:outline-none resize-none"
            />
            <div className="text-xs text-ink-muted mt-1">{t("admin.notes_hint")}</div>
          </section>

          {/* Danger zone */}
          <section className="border-t border-white/10 pt-4">
            <h3 className="text-xs uppercase tracking-wider text-err mb-3 font-medium flex items-center gap-1.5">
              <AlertTriangle size={12} /> {t("admin.section_danger")}
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={toggleSuspend} disabled={saving} data-testid="modal-suspend">
                {user.is_active ? <Ban size={14} /> : <Power size={14} />}
                {user.is_active ? t("admin.suspend") : t("admin.reactivate")}
              </Button>
              <Button variant="danger" onClick={deleteUser} disabled={saving} data-testid="modal-delete">
                <X size={14} /> {t("admin.delete_account")}
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// -------------------- Tabs --------------------
function TabButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
        active ? "bg-brand/15 text-brand" : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
      }`}
    >
      <Icon size={14} strokeWidth={1.6} />
      {children}
    </button>
  );
}

// ===================== Main =====================
export default function AdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [settings, setSettings] = useState(null);
  const [demos, setDemos] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [duplicates, setDuplicates] = useState(null); // {items, total} | null
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [setupOrders, setSetupOrders] = useState(null);
  const [affFilter, setAffFilter] = useState("");
  const [affSearch, setAffSearch] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, a, r, st, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/ai-usage"),
        api.get("/admin/revenue"),
        api.get("/admin/settings"),
        api.get("/admin/demos"),
      ]);
      setStats(s.data);
      setAiUsage(a.data);
      setRevenue(r.data);
      setSettings(st.data);
      setDemos(d.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try {
      const u = await api.get("/admin/users", {
        params: { search: search || undefined, plan: planFilter || undefined, role: roleFilter || undefined },
      });
      setUsers(u.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadDuplicates = async () => {
    try {
      const r = await api.get("/admin/duplicates");
      setDuplicates(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const mergeDuplicate = async (id) => {
    if (!window.confirm("Diese Zutat löschen und alle Verweise auf das Original umbiegen?")) return;
    try {
      const r = await api.post(`/admin/duplicates/${id}/merge`);
      toast.success(`Zusammengeführt. Rezepte: ${r.data.recipes_updated}, Cocktails: ${r.data.cocktails_updated}.`);
      await loadDuplicates(); await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dismissDuplicate = async (id) => {
    try {
      await api.post(`/admin/duplicates/${id}/dismiss`);
      toast.success("Als kein Duplikat markiert.");
      await loadDuplicates(); await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadAffiliates = async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/admin/affiliates", { params: { status: affFilter || undefined, q: affSearch || undefined } }),
        api.get("/admin/payouts"),
      ]);
      setAffiliates(a.data);
      setPayouts(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setAffiliateStatus = async (affiliate, status) => {
    try {
      await api.post(`/admin/affiliates/${affiliate.id}/status`, { status });
      toast.success(t("admin.affiliate_status_updated"));
      loadAffiliates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markPayoutPaid = async (p) => {
    const ref = window.prompt(t("admin.payout_paid_prompt"), "");
    if (ref === null) return;
    try {
      await api.post(`/admin/payouts/${p.id}/mark-paid`, { paid_reference: ref });
      toast.success(t("admin.payout_marked_paid"));
      loadAffiliates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rejectPayout = async (p) => {
    if (!window.confirm(t("admin.payout_reject_confirm"))) return;
    try {
      await api.post(`/admin/payouts/${p.id}/reject`);
      toast.success(t("admin.payout_rejected"));
      loadAffiliates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (tab !== "users") return;
    const t = setTimeout(loadUsers, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, planFilter, roleFilter, tab]);

  useEffect(() => {
    if (tab !== "affiliates") return;
    const t = setTimeout(loadAffiliates, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affFilter, affSearch, tab]);

  useEffect(() => {
    if (tab !== "setup-orders") return;
    (async () => {
      try {
        const { data } = await api.get("/admin/setup-orders");
        setSetupOrders(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const markSetupPaid = async (id) => {
    try {
      await api.post(`/admin/setup-orders/${id}/mark-paid`, {});
      toast.success(t("admin.setup_marked_paid"));
      const { data } = await api.get("/admin/setup-orders");
      setSetupOrders(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUserSaved = async () => { await Promise.all([loadAll(), loadUsers()]); };

  const toggleDemoSignup = async (next) => {
    try {
      const { data } = await api.patch("/admin/settings", { demo_signup_open: next });
      setSettings(data);
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading && !stats) {
    return <div className="flex justify-center py-16"><Spinner size={28} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">
            {t("admin.kicker")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-ink-primary" data-testid="admin-title">
            {t("admin.title")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={loadAll} data-testid="admin-refresh">
            <RefreshCw size={14} /> {t("admin.refresh")}
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-1 border-b border-white/10 pb-2">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} icon={BarChart3} testId="tab-overview">{t("admin.tab_overview")}</TabButton>
        <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={Users} testId="tab-users">{t("admin.tab_users")}</TabButton>
        <TabButton active={tab === "demos"} onClick={() => setTab("demos")} icon={BedDouble} testId="tab-demos">{t("admin.tab_demos")}</TabButton>
        <TabButton active={tab === "ai"} onClick={() => setTab("ai")} icon={Sparkles} testId="tab-ai">{t("admin.tab_ai")}</TabButton>
        <TabButton active={tab === "revenue"} onClick={() => setTab("revenue")} icon={TrendingUp} testId="tab-revenue">{t("admin.tab_revenue")}</TabButton>
        <TabButton active={tab === "affiliates"} onClick={() => setTab("affiliates")} icon={Handshake} testId="tab-affiliates">{t("admin.tab_affiliates")}</TabButton>
        <TabButton active={tab === "setup-orders"} onClick={() => setTab("setup-orders")} icon={BadgeEuro} testId="tab-setup-orders">{t("admin.tab_setup_orders")}</TabButton>
        <TabButton active={tab === "duplicates"} onClick={() => { setTab("duplicates"); loadDuplicates(); }} icon={GitMerge} testId="tab-duplicates">
          Mögliche Duplikate {stats?.potential_duplicates > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] bg-warn/20 text-warn px-1.5 py-0.5 rounded-full num">{stats.potential_duplicates}</span>}
        </TabButton>
      </div>

      {/* ============ Overview ============ */}
      {tab === "overview" && stats && revenue && (
        <div className="space-y-6" data-testid="admin-overview">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi icon={Users} label={t("admin.kpi_total")} value={stats.total_users} testId="kpi-total" />
            <Kpi icon={BadgeEuro} label="MRR" value={formatEUR(stats.mrr)} sub={`ARR ${formatEUR(stats.arr)}`} tone="brand" testId="kpi-mrr" />
            <Kpi icon={Activity} label={t("admin.kpi_paying")} value={stats.paying_users} sub={`${stats.demo_users} demos`} tone="ok" testId="kpi-paying" />
            <Kpi icon={ShieldCheck} label={t("admin.kpi_new_month")} value={stats.new_this_month} testId="kpi-new" />
            <Kpi icon={FileWarning} label={t("admin.kpi_churn")} value={`${stats.churn_rate}%`} sub={t("admin.kpi_churn_sub")} tone={stats.churn_rate > 5 ? "warn" : "default"} testId="kpi-churn" />
          </div>

          <Card className="p-5" data-testid="overview-chart">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("admin.revenue_12m")}</div>
                <div className="text-base font-medium text-ink-primary mt-0.5">{t("admin.revenue_chart_title")}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-ink-secondary">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-brand rounded-sm" /> {t("admin.subscription")}</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-purple-500 rounded-sm" /> {t("admin.topup")}</span>
              </div>
            </div>
            <MRRBarChart data={revenue.history} />
          </Card>
        </div>
      )}

      {/* ============ Users ============ */}
      {tab === "users" && (
        <div className="space-y-4" data-testid="admin-users">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input className="pl-9" placeholder={t("admin.search_placeholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="users-search" />
            </div>
            <Select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} data-testid="users-plan-filter" className="w-32">
              <option value="">{t("admin.all_plans")}</option>
              <option>none</option><option>kueche</option><option>bar</option>
              <option>pro</option><option>hotel</option><option>premium</option>
            </Select>
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="users-role-filter" className="w-32">
              <option value="">{t("admin.all_roles")}</option>
              <option>gastronom</option><option>super_admin</option>
            </Select>
            <Button onClick={() => setShowCreate(true)} data-testid="admin-create-user-btn">
              <Plus size={14} /> Kunde anlegen
            </Button>
          </div>

          <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-ink-muted text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2.5">{t("admin.col_email")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_plan")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_status")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.col_credits")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_last_login")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_created")}</th>
                    <th className="px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 text-ink-primary">
                        <div className="font-medium truncate max-w-[260px]">{u.email}</div>
                        {u.business_name && <div className="text-[11px] text-ink-muted truncate max-w-[260px]">{u.business_name}</div>}
                      </td>
                      <td className="px-3 py-2.5"><Badge tone="brand">{u.plan || "none"}</Badge></td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          <Badge tone={u.is_active ? "ok" : "err"}>{u.is_active ? t("admin.active") : t("admin.suspended")}</Badge>
                          {u.demo_mode && <Badge tone="warn">DEMO</Badge>}
                          {u.role === "super_admin" && <Badge>admin</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right num text-ink-secondary">{(u.ai_credits || 0) + (u.purchased_credits || 0)}</td>
                      <td className="px-3 py-2.5 text-ink-muted">{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</td>
                      <td className="px-3 py-2.5 text-ink-muted">{u.created_at ? formatDate(u.created_at) : "—"}</td>
                      <td className="px-2 py-2.5">
                        <button onClick={() => setEditing(u)} className="text-ink-secondary hover:text-brand p-1" data-testid={`edit-user-${u.id}`}>
                          <ChevronRight size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!users.length && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-muted">{t("admin.no_users")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============ Demos ============ */}
      {tab === "demos" && settings && (
        <div className="space-y-4" data-testid="admin-demos">
          <Card className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("admin.global_settings")}</div>
                <div className="text-base font-medium text-ink-primary mt-0.5">{t("admin.demo_signup_open")}</div>
                <div className="text-xs text-ink-muted mt-1 max-w-md">{t("admin.demo_signup_help")}</div>
              </div>
              <button
                onClick={() => toggleDemoSignup(!settings.demo_signup_open)}
                data-testid="toggle-demo-signup"
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${settings.demo_signup_open ? "bg-ok" : "bg-white/15"}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${settings.demo_signup_open ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          </Card>

          <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="text-sm font-medium text-ink-primary">{t("admin.active_demos")}</div>
              <div className="text-xs text-ink-muted">{demos.length} {t("admin.demos_total")}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-ink-muted text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2.5">{t("admin.col_email")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_plan")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_expires")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.days_left")}</th>
                    <th className="px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {demos.map((d) => (
                    <tr key={d.id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 text-ink-primary truncate max-w-[260px]">{d.email}</td>
                      <td className="px-3 py-2.5"><Badge tone="brand">{d.plan || "none"}</Badge></td>
                      <td className="px-3 py-2.5 text-ink-secondary">{d.demo_expires_at ? formatDateTime(d.demo_expires_at) : "—"}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Badge tone={d.expired ? "err" : (d.days_left <= 3 ? "warn" : "default")}>
                          {d.expired ? t("admin.expired") : `${d.days_left}d`}
                        </Badge>
                      </td>
                      <td className="px-2 py-2.5">
                        <button onClick={() => setEditing(d)} className="text-ink-secondary hover:text-brand p-1" data-testid={`edit-demo-${d.id}`}>
                          <ChevronRight size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!demos.length && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-ink-muted">{t("admin.no_demos")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============ AI Usage ============ */}
      {tab === "ai" && aiUsage && (
        <div className="space-y-4" data-testid="admin-ai">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={Sparkles} label={t("admin.ai_mtd")} value={aiUsage.month_to_date_credits} tone="brand" testId="ai-kpi-mtd" />
            <Kpi icon={Activity} label={t("admin.ai_today")} value={aiUsage.today_credits} testId="ai-kpi-today" />
            <Kpi icon={BadgeEuro} label={t("admin.ai_cost_mtd")} value={formatEUR(aiUsage.estimated_cost_mtd_eur)} sub={`${formatEUR(aiUsage.estimated_cost_per_credit_eur)} ${t("admin.per_credit")}`} tone="ok" testId="ai-kpi-cost" />
            <Kpi icon={AlertTriangle} label={t("admin.ai_failures")} value={aiUsage.failures_this_month} tone={aiUsage.failures_this_month > 0 ? "warn" : "default"} testId="ai-kpi-fails" />
          </div>

          <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5">
              <div className="text-sm font-medium text-ink-primary">{t("admin.top10_title")}</div>
              <div className="text-xs text-ink-muted mt-0.5">{t("admin.top10_sub")}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-top-table">
                <thead className="bg-white/[0.02] text-ink-muted text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2.5">#</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_email")}</th>
                    <th className="text-left px-3 py-2.5">{t("admin.col_plan")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.credits")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.est_cost")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {aiUsage.top_users.map((u, i) => (
                    <tr key={u.user_id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 num text-ink-muted">{i + 1}</td>
                      <td className="px-3 py-2.5 text-ink-primary truncate max-w-[260px]">{u.email}</td>
                      <td className="px-3 py-2.5"><Badge tone="brand">{u.plan || "none"}</Badge></td>
                      <td className="px-3 py-2.5 text-right num text-ink-primary">{u.credits}</td>
                      <td className="px-3 py-2.5 text-right num text-ink-secondary">{formatEUR(u.estimated_cost_eur)}</td>
                    </tr>
                  ))}
                  {!aiUsage.top_users.length && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-ink-muted">{t("admin.ai_none")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============ Revenue ============ */}
      {tab === "revenue" && revenue && (
        <div className="space-y-4" data-testid="admin-revenue">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={BadgeEuro} label="MRR" value={formatEUR(stats?.mrr ?? 0)} tone="brand" testId="rev-kpi-mrr" />
            <Kpi icon={TrendingUp} label="ARR" value={formatEUR(stats?.arr ?? 0)} tone="ok" testId="rev-kpi-arr" />
            <Kpi icon={Activity} label={t("admin.subscription_total")} value={formatEUR(revenue.totals.subscription_eur)} sub={`${revenue.totals.subscription_count} ${t("admin.payments")}`} testId="rev-kpi-sub" />
            <Kpi icon={Sparkles} label={t("admin.topup_total")} value={formatEUR(revenue.totals.topup_eur)} sub={`${revenue.totals.topup_count} ${t("admin.payments")}`} testId="rev-kpi-topup" />
          </div>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("admin.revenue_12m")}</div>
                <div className="text-base font-medium text-ink-primary mt-0.5">{t("admin.revenue_chart_title")}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-ink-secondary">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-brand rounded-sm" /> {t("admin.subscription")}</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-purple-500 rounded-sm" /> {t("admin.topup")}</span>
              </div>
            </div>
            <MRRBarChart data={revenue.history} />
          </Card>

          <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-ink-muted text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2.5">{t("admin.month")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.subscription")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.topup")}</th>
                    <th className="text-right px-3 py-2.5">{t("admin.total")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {revenue.history.map((b) => (
                    <tr key={b.label} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 text-ink-primary">{b.label}</td>
                      <td className="px-3 py-2.5 text-right num text-ink-secondary">{formatEUR(b.subscription_eur)}</td>
                      <td className="px-3 py-2.5 text-right num text-ink-secondary">{formatEUR(b.topup_eur)}</td>
                      <td className="px-3 py-2.5 text-right num text-ink-primary font-medium">{formatEUR(b.total_eur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============ Affiliates ============ */}
      {tab === "affiliates" && (
        <div className="space-y-6" data-testid="admin-affiliates">
          {/* Summary KPIs */}
          {affiliates && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Kpi icon={Handshake} label={t("admin.aff_total")} value={affiliates.summary?.total ?? 0} testId="aff-kpi-total" />
              <Kpi icon={AlertTriangle} label={t("admin.aff_pending")} value={affiliates.summary?.pending ?? 0} tone="warn" testId="aff-kpi-pending" />
              <Kpi icon={Check} label={t("admin.aff_active")} value={affiliates.summary?.active ?? 0} tone="ok" testId="aff-kpi-active" />
              <Kpi icon={Wallet} label={t("admin.aff_payout_requested")} value={formatEUR(payouts?.totals?.requested_eur ?? 0)} tone="warn" testId="aff-kpi-payout-req" />
              <Kpi icon={Wallet} label={t("admin.aff_payout_paid")} value={formatEUR(payouts?.totals?.paid_eur ?? 0)} tone="ok" testId="aff-kpi-payout-paid" />
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px] max-w-md">
              <Label>{t("admin.aff_search")}</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <Input value={affSearch} onChange={(e) => setAffSearch(e.target.value)} placeholder={t("admin.aff_search_ph")} className="pl-9" data-testid="aff-search" />
              </div>
            </div>
            <div>
              <Label>{t("admin.aff_filter_status")}</Label>
              <Select value={affFilter} onChange={(e) => setAffFilter(e.target.value)} data-testid="aff-filter-status">
                <option value="">{t("common.all")}</option>
                <option value="pending">{t("admin.aff_status_pending")}</option>
                <option value="active">{t("admin.aff_status_active")}</option>
                <option value="rejected">{t("admin.aff_status_rejected")}</option>
                <option value="suspended">{t("admin.aff_status_suspended")}</option>
              </Select>
            </div>
            <Button variant="secondary" size="sm" onClick={loadAffiliates} data-testid="aff-refresh">
              <RefreshCw size={14} /> {t("admin.refresh")}
            </Button>
          </div>

          {/* Pending payouts */}
          {payouts?.items?.filter(p => p.status === "requested").length > 0 && (
            <div className="bg-bg-surface border border-warn/30 rounded-xl">
              <div className="px-4 py-3 border-b border-white/5 text-sm font-semibold flex items-center gap-2">
                <Wallet size={14} className="text-warn" /> {t("admin.payouts_pending")}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payouts-pending-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted bg-white/[0.02]">
                      <th className="px-4 py-2">{t("admin.aff_table_name")}</th>
                      <th className="px-4 py-2">{t("admin.aff_table_email")}</th>
                      <th className="px-4 py-2 text-right">{t("admin.aff_payout_amount")}</th>
                      <th className="px-4 py-2">{t("admin.aff_payout_requested")}</th>
                      <th className="px-4 py-2 text-right">{t("common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {payouts.items.filter(p => p.status === "requested").map((p) => (
                      <tr key={p.id} data-testid={`payout-row-${p.id}`}>
                        <td className="px-4 py-2.5 text-ink-primary">{p.affiliate_name}</td>
                        <td className="px-4 py-2.5 text-ink-secondary text-xs">{p.affiliate_email} · {p.payout_email}</td>
                        <td className="px-4 py-2.5 text-right num font-medium">{formatEUR(p.amount_eur)}</td>
                        <td className="px-4 py-2.5 text-ink-secondary">{formatDate(p.requested_at)}</td>
                        <td className="px-4 py-2.5 text-right space-x-1">
                          <Button variant="secondary" size="sm" onClick={() => markPayoutPaid(p)} data-testid={`payout-mark-paid-${p.id}`}>
                            <Check size={12} /> {t("admin.mark_paid")}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => rejectPayout(p)} data-testid={`payout-reject-${p.id}`}>
                            <Ban size={12} /> {t("admin.reject")}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Affiliates list */}
          <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="affiliates-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted bg-white/[0.02]">
                    <th className="px-4 py-2.5">{t("admin.aff_table_name")}</th>
                    <th className="px-4 py-2.5">{t("admin.aff_table_email")}</th>
                    <th className="px-4 py-2.5">{t("admin.aff_table_code")}</th>
                    <th className="px-4 py-2.5">{t("admin.aff_table_status")}</th>
                    <th className="px-4 py-2.5 text-right">{t("admin.aff_table_referrals")}</th>
                    <th className="px-4 py-2.5 text-right">{t("admin.aff_table_pending")}</th>
                    <th className="px-4 py-2.5 text-right">{t("admin.aff_table_lifetime")}</th>
                    <th className="px-4 py-2.5 text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {affiliates?.items?.length ? affiliates.items.map((a) => (
                    <tr key={a.id} className="hover:bg-white/[0.02]" data-testid={`aff-row-${a.id}`}>
                      <td className="px-4 py-2.5 text-ink-primary">{a.name}</td>
                      <td className="px-4 py-2.5 text-ink-secondary text-xs">{a.email}</td>
                      <td className="px-4 py-2.5 text-ink-secondary font-mono text-xs">{a.referral_code}</td>
                      <td className="px-4 py-2.5">
                        <Badge tone={a.status === "active" ? "ok" : a.status === "pending" ? "warn" : "err"}>{t(`admin.aff_status_${a.status}`, a.status)}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right num text-ink-secondary">{a.referrals} / {a.converted}</td>
                      <td className="px-4 py-2.5 text-right num text-warn">{formatEUR(a.balance?.pending_eur ?? 0)}</td>
                      <td className="px-4 py-2.5 text-right num text-ink-primary">{formatEUR(a.balance?.lifetime_eur ?? 0)}</td>
                      <td className="px-4 py-2.5 text-right space-x-1">
                        {a.status === "pending" && (
                          <>
                            <Button variant="secondary" size="sm" onClick={() => setAffiliateStatus(a, "active")} data-testid={`aff-approve-${a.id}`}>
                              <Check size={12} /> {t("admin.approve")}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setAffiliateStatus(a, "rejected")} data-testid={`aff-reject-${a.id}`}>
                              <Ban size={12} />
                            </Button>
                          </>
                        )}
                        {a.status === "active" && (
                          <Button variant="ghost" size="sm" onClick={() => setAffiliateStatus(a, "suspended")} data-testid={`aff-suspend-${a.id}`}>
                            <Ban size={12} /> {t("admin.suspend")}
                          </Button>
                        )}
                        {(a.status === "suspended" || a.status === "rejected") && (
                          <Button variant="secondary" size="sm" onClick={() => setAffiliateStatus(a, "active")} data-testid={`aff-reactivate-${a.id}`}>
                            <Power size={12} /> {t("admin.reactivate")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-muted">{t("admin.aff_empty")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============ Setup Orders ============ */}
      {tab === "setup-orders" && (
        <div className="space-y-6" data-testid="admin-setup-orders">
          {setupOrders && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi icon={BadgeEuro} label={t("admin.setup_total")} value={setupOrders.totals?.total ?? 0} testId="setup-kpi-total" />
              <Kpi icon={AlertTriangle} label={t("admin.setup_pending")} value={setupOrders.totals?.pending ?? 0} tone="warn" testId="setup-kpi-pending" />
              <Kpi icon={Check} label={t("admin.setup_paid")} value={setupOrders.totals?.paid ?? 0} tone="ok" testId="setup-kpi-paid" />
              <Kpi icon={BadgeEuro} label={t("admin.setup_revenue")} value={formatEUR(setupOrders.totals?.revenue_eur ?? 0)} tone="ok" testId="setup-kpi-revenue" />
            </div>
          )}
          <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="setup-orders-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted bg-white/[0.02]">
                    <th className="px-4 py-2.5">{t("admin.setup_col_customer")}</th>
                    <th className="px-4 py-2.5">{t("admin.setup_col_package")}</th>
                    <th className="px-4 py-2.5 text-right">{t("admin.setup_col_amount")}</th>
                    <th className="px-4 py-2.5">{t("admin.setup_col_status")}</th>
                    <th className="px-4 py-2.5">{t("admin.setup_col_created")}</th>
                    <th className="px-4 py-2.5 text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {setupOrders?.items?.length ? setupOrders.items.map((o) => (
                    <tr key={o.id} data-testid={`setup-row-${o.id}`}>
                      <td className="px-4 py-2.5 text-ink-primary">{o.user_business} <span className="text-xs text-ink-muted">{o.user_email}</span></td>
                      <td className="px-4 py-2.5 text-ink-secondary">{o.package_name}</td>
                      <td className="px-4 py-2.5 text-right num">{formatEUR(o.amount_eur)}</td>
                      <td className="px-4 py-2.5"><Badge tone={o.status === "paid" ? "ok" : "warn"}>{o.status}</Badge></td>
                      <td className="px-4 py-2.5 text-xs text-ink-muted">{formatDate(o.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {o.status !== "paid" && (
                          <Button variant="secondary" size="sm" onClick={() => markSetupPaid(o.id)} data-testid={`setup-mark-paid-${o.id}`}>
                            <Check size={12} /> {t("admin.mark_paid")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-muted">{t("admin.setup_empty")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "duplicates" && (
        <div className="space-y-4" data-testid="admin-duplicates">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight">Mögliche Duplikate</h2>
            <Button variant="secondary" size="sm" onClick={loadDuplicates}><RefreshCw size={12} /> Neu laden</Button>
          </div>
          {!duplicates && <Spinner />}
          {duplicates && duplicates.items.length === 0 && (
            <Card className="p-10 text-center text-sm text-ink-muted">Keine möglichen Duplikate gefunden.</Card>
          )}
          {duplicates && duplicates.items.length > 0 && (
            <Card className="overflow-hidden">
              <table className="w-full text-sm" data-testid="duplicates-table">
                <thead className="bg-white/[0.02] text-ink-muted text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2.5">Neue Zutat</th>
                    <th className="text-left px-3 py-2.5">Mögliches Original</th>
                    <th className="text-left px-3 py-2.5">Kunde</th>
                    <th className="text-left px-3 py-2.5">Angelegt</th>
                    <th className="text-right px-3 py-2.5">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {duplicates.items.map((d) => (
                    <tr key={d.id} data-testid={`duplicate-row-${d.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-ink-primary">{d.name}</div>
                        <div className="text-[11px] text-ink-muted">{d.unit} · {(d.price_per_unit || 0).toFixed(4)} €/{d.unit}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        {d.duplicate_of ? (
                          <>
                            <div className="font-medium text-ink-primary">{d.duplicate_of.name}</div>
                            <div className="text-[11px] text-ink-muted">{d.duplicate_of.unit} · {(d.duplicate_of.price_per_unit || 0).toFixed(4)}</div>
                          </>
                        ) : <span className="text-ink-muted">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-ink-primary">{d.owner_business || "—"}</div>
                        <div className="text-[11px] text-ink-muted">{d.owner_email}</div>
                      </td>
                      <td className="px-3 py-2.5 text-ink-muted">{d.created_at ? formatDate(d.created_at) : "—"}</td>
                      <td className="px-3 py-2.5 text-right space-x-1">
                        <Button size="sm" variant="secondary" disabled={!d.duplicate_of} onClick={() => mergeDuplicate(d.id)} data-testid={`merge-${d.id}`}>
                          <GitMerge size={12} /> Merge
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => dismissDuplicate(d.id)} data-testid={`dismiss-${d.id}`}>
                          <X size={12} /> Kein Duplikat
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {editing && (
        <div onClick={() => setEditing(null)}>
          <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={onUserSaved} />
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await loadUsers(); await loadAll(); }}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", name: "", business_name: "",
    password: "", auto_password: true,
    plan: "kueche", ai_credits: 20, is_active: true,
    role: "gastronom",
    demo_mode: false, demo_days: 14,
    language: "de",
    internal_notes: "",
    send_welcome_email: true,
  });
  const [busy, setBusy] = useState(false);

  // Auto-generate password on mount
  useEffect(() => {
    if (form.auto_password && !form.password) {
      const rnd = Math.random().toString(36).slice(2, 10);
      setForm((s) => ({ ...s, password: `Sof${rnd}!` }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name,
        business_name: form.business_name,
        role: form.role,
        plan: form.plan,
        ai_credits: Number(form.ai_credits) || 0,
        is_active: form.is_active,
        demo_mode: form.demo_mode,
        demo_days: form.demo_mode ? Number(form.demo_days) : null,
        language: form.language,
        internal_notes: form.internal_notes,
        send_welcome_email: form.send_welcome_email,
      };
      await api.post("/admin/users", body);
      toast.success("Kunde angelegt.");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-bg-surface border border-white/10 rounded-2xl max-w-xl w-full p-6 shadow-glow space-y-4 max-h-[90vh] overflow-y-auto"
        data-testid="admin-create-user-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold tracking-tight">Neuen Kunden anlegen</h3>
          <button type="button" onClick={onClose} className="text-ink-secondary hover:text-ink-primary"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>E-Mail</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="create-email" />
          </div>
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="create-name" /></div>
          <div><Label>Geschäftsname</Label><Input value={form.business_name} onChange={(e) => setForm((s) => ({ ...s, business_name: e.target.value }))} data-testid="create-business" /></div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 mb-1.5 text-xs text-ink-secondary">
              <input type="checkbox" checked={form.auto_password} onChange={(e) => setForm((s) => ({ ...s, auto_password: e.target.checked }))} data-testid="create-autopw" /> Passwort automatisch generieren
            </label>
            <Label>Passwort</Label>
            <Input required minLength={8} value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value, auto_password: false }))} data-testid="create-password" />
          </div>
          <div>
            <Label>Plan</Label>
            <Select value={form.plan} onChange={(e) => setForm((s) => ({ ...s, plan: e.target.value }))} data-testid="create-plan">
              <option value="none">none</option>
              <option value="kueche">Küche</option>
              <option value="bar">Bar</option>
              <option value="pro">Pro</option>
              <option value="hotel">Hotel</option>
              <option value="premium">Premium</option>
            </Select>
          </div>
          <div><Label>AI Credits</Label><Input type="number" min={0} value={form.ai_credits} onChange={(e) => setForm((s) => ({ ...s, ai_credits: e.target.value }))} data-testid="create-credits" /></div>
          <div>
            <Label>Sprache</Label>
            <Select value={form.language} onChange={(e) => setForm((s) => ({ ...s, language: e.target.value }))}>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </Select>
          </div>
          <div>
            <Label>Rolle</Label>
            <Select value={form.role} onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}>
              <option value="gastronom">gastronom</option>
              <option value="super_admin">super_admin</option>
            </Select>
          </div>
          <div className="col-span-2 flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-ink-secondary">
              <input type="checkbox" checked={form.demo_mode} onChange={(e) => setForm((s) => ({ ...s, demo_mode: e.target.checked }))} data-testid="create-demo" /> Demo-Modus
            </label>
            {form.demo_mode && (
              <div className="flex items-center gap-2">
                <Label className="!mb-0">Demo-Tage</Label>
                <Input type="number" min={1} max={365} className="w-20" value={form.demo_days} onChange={(e) => setForm((s) => ({ ...s, demo_days: e.target.value }))} data-testid="create-demo-days" />
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Interne Notiz</Label>
            <textarea
              rows={2}
              className="w-full bg-bg-void border border-white/10 rounded-md p-2 text-sm text-ink-primary"
              value={form.internal_notes}
              onChange={(e) => setForm((s) => ({ ...s, internal_notes: e.target.value }))}
              data-testid="create-notes"
            />
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-xs text-ink-secondary">
              <input type="checkbox" checked={form.send_welcome_email} onChange={(e) => setForm((s) => ({ ...s, send_welcome_email: e.target.checked }))} data-testid="create-send-welcome" /> Begrüßungs-E-Mail senden (mit Passwort)
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button type="submit" disabled={busy} data-testid="create-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Anlegen
          </Button>
        </div>
      </form>
    </div>
  );
}
